/* eslint-disable @typescript-eslint/no-explicit-any */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

let ffmpegInstance: any | null = null;
let ffmpegLoadPromise: Promise<any> | null = null;

/** Lazy-load and initialize FFmpeg. */
export async function getFFmpeg(): Promise<any> {
  if (ffmpegInstance) return ffmpegInstance;
  if (ffmpegLoadPromise) return ffmpegLoadPromise;

  ffmpegLoadPromise = (async () => {
    const ff = new FFmpeg();

    ff.on('log', ({ message }: { message: string }) => {
      console.log('[FFmpeg Log]', message);
    });

    try {
      // Use UMD build for Create React App (ESM is for Vite)
      const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';
      const jsURL = `${baseURL}/ffmpeg-core.js`;
      const wasmURL = `${baseURL}/ffmpeg-core.wasm`;

      await ff.load({
        coreURL: jsURL,
        wasmURL: wasmURL,
      });

      ffmpegInstance = ff;
      return ff;
    } catch (err) {
      ffmpegLoadPromise = null;
      const errMsg = err instanceof Error ? err.message : String(err);
      throw new Error(`Failed to load FFmpeg: ${errMsg}`);
    }
  })();

  return ffmpegLoadPromise;
}

/**
 * Convert a WebM blob to MP4 using FFmpeg.
 * @param webmBlob - The WebM video blob
 * @param outputName - Desired output filename without extension (not used, kept for backward compatibility)
 * @param onProgress - Optional callback for conversion progress (0.0 to 1.0)
 * @param signal - Optional AbortSignal to cancel conversion
 * @returns MP4 blob
 */
export async function convertWebMToMP4(
  webmBlob: Blob,
  outputName: string,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const inputFileName = 'input.webm';
  const outputFileName = 'output.mp4';

  if (signal?.aborted) {
    throw new DOMException('MP4 conversion cancelled', 'AbortError');
  }

  try {
    const ff = await getFFmpeg();

    let aborted = false;
    const handleAbort = () => {
      aborted = true;
      // Terminate FFmpeg to interrupt the exec() call
      ff.terminate();
      ffmpegInstance = null;
      ffmpegLoadPromise = null;
    };

    signal?.addEventListener('abort', handleAbort);

    try {
      // Listen to progress events
      let lastProgress = 0;
      ff.on('progress', ({ progress }: { progress: number }) => {
        lastProgress = progress;
        if (onProgress) onProgress(progress);
      });

      // Write the WebM blob to FFmpeg's virtual filesystem
      const webmData = await fetchFile(webmBlob);
      await ff.writeFile(inputFileName, webmData);

      // Simple WebM to MP4 conversion
      // https://ffmpegwasm.netlify.app/docs/getting-started/usage/#transcode-webm-to-mp4-video
      await ff.exec(['-i', inputFileName, outputFileName]);

      // If aborted, throw even if exec succeeded
      if (aborted) {
        throw new DOMException('MP4 conversion cancelled', 'AbortError');
      }

      // Ensure progress reaches 100%
      if (onProgress) onProgress(1.0);

      // Read the output file
      const data = await ff.readFile(outputFileName);

      // Cleanup
      ff.deleteFile(inputFileName);
      ff.deleteFile(outputFileName);

      // Use data.buffer when creating the Blob
      return new Blob([data.buffer], { type: 'video/mp4' });
    } finally {
      signal?.removeEventListener('abort', handleAbort);
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Unload FFmpeg to free up resources.
 */
export function unloadFFmpeg(): void {
  if (ffmpegInstance) {
    ffmpegInstance.terminate();
    ffmpegInstance = null;
    ffmpegLoadPromise = null;
  }
}
